import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ExternalLink, Settings } from 'lucide-react';
import { PageLayout } from '../../components/ui/PageLayout';
import Button from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { statusVariantMap } from './types';

// Components
import InteractionsTab from './components/InteractionsTab';
import PersonaSelector from './components/PersonaSelector';
import TabNavigation from './components/TabNavigation';
import PublicURLSection from './components/PublicURLSection';
import ProfileInfoSection from './components/ProfileInfoSection';
import AchievementsSection from './components/AchievementsSection';
import SocialLinksSection from './components/SocialLinksSection';
import PrivacySettings from './components/PrivacySettings';
import LivePreview from './components/LivePreview';
import StatusNotices from './components/StatusNotices';
import StickyActionBar from '../../components/ui/StickyActionBar';
import { Card } from '../../components/ui/Card';
import Snackbar, { CopiedToast } from './components/Snackbar';
import { VisibilityAcknowledgmentModal, ShareModal } from './components/Modals';

// Hooks
import { useProfileConfiguration } from './hooks/useProfileConfiguration';

const ProfileConfigurationCenter: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userId } = useParams<{ userId?: string }>();

  const {
    // State
    formData,
    profile,
    personas,
    contacts,
    selectedPersona,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    errors,
    snackbar,
    copiedToast,
    showShareMenu,
    showVisibilityAcknowledgment,
    editingAchievementIndex,
    activeTab,
    newAchievement,
    profileUrl,
    currentUser,
    selectedPersonaData,
    settingsStatus,

    // Actions
    setActiveTab,
    setShowShareMenu,
    setShowVisibilityAcknowledgment,
    setSnackbar,
    setNewAchievement,
    handleInputChange,
    handlePersonaChange,
    handleSave,
    handleReset,
    handlePublicToggle,
    handleAcknowledgePublic,
    handleCopyLink,
    handleRegenerateLink,
    handleDownloadVCard,
    handleAddAchievement,
    handleEditAchievement,
    handleUpdateAchievement,
    handleSaveAchievementEdit,
    handleDeleteAchievement,
    handleMoveAchievement,
    handlePreviewAsExternal,
    // Social Links
    handleAddSocialLink,
    handleUpdateSocialLink,
    handleDeleteSocialLink,
    handleMoveSocialLink,
    isFieldAllowed,
    // HR editing mode
    isHrEditingMode,
    targetUserName,
    // Branding
    companyBranding,
    publicProfileDetail,
  } = useProfileConfiguration(t, navigate, userId);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <div className="pt-8 pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card variant="elevated" className="p-12 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="ms-3 text-muted-foreground">{t('Loading profile...')}</span>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Profiles disabled by HR settings
  if (settingsStatus && !settingsStatus.isProfilesEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <div className="pt-8 pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card variant="elevated" className="p-12 text-center">
              <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">{t('Public Profiles Disabled')}</h2>
              <p className="text-muted-foreground mb-4">
                {settingsStatus.disabledReason || t('Public profiles are currently disabled for your account.')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('Please contact your HR administrator for more information.')}
              </p>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10">
      <PageLayout
        title={t('Profile Configuration')}
        icon={Settings}
        accentColor="purple"
        maxWidth="1600px"
        headerActions={
          <div className="flex items-center gap-3">
            <StatusBadge variant={statusVariantMap[formData.profileStatus] || 'neutral'}>
              {(formData.profileStatus === 'active' ? 'Public' : formData.profileStatus)?.charAt(0)?.toUpperCase() + (formData.profileStatus === 'active' ? 'Public' : formData.profileStatus)?.slice(1)}
            </StatusBadge>
            <Button
              variant="outline"
              onClick={handlePreviewAsExternal}
              className="gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              {t('Preview as External Visitor')}
            </Button>
          </div>
        }
      >
          {/* HR Editing Mode Banner */}
          {isHrEditingMode && (
            <div className="mb-6 bg-warning/10 border border-warning/20 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" />
                <div>
                  <p className="font-medium text-foreground">
                    {t('HR Admin Mode')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('You are editing an employee\'s public profile as HR administrator.')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Persona Selector */}
          <PersonaSelector
            personas={personas}
            selectedPersona={selectedPersona}
            selectedPersonaData={selectedPersonaData}
            onPersonaChange={handlePersonaChange}
            t={t}
          />

          {/* Tab Navigation */}
          <TabNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            t={t}
          />

          {/* Status Notices */}
          <StatusNotices
            isPublic={formData.isPublic}
            profileStatus={formData.profileStatus}
            activeTab={activeTab}
            t={t}
          />

          {/* Tab Content */}
          {activeTab === 'configuration' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Panel - Configuration Form */}
              <div className="space-y-6">
                <PublicURLSection
                  publicSlug={formData.publicSlug}
                  profileUrl={profileUrl}
                  copiedToast={copiedToast}
                  canRegenerateLink={formData.hrPolicies.canRegenerateLink}
                  isPublic={formData.isPublic}
                  profileStatus={formData.profileStatus}
                  onCopyLink={handleCopyLink}
                  onShare={() => setShowShareMenu(true)}
                  onRegenerateLink={handleRegenerateLink}
                  onDownloadVCard={handleDownloadVCard}
                  onPublicToggle={handlePublicToggle}
                  t={t}
                />

                <ProfileInfoSection
                  bio={formData.bio}
                  jobTitle={formData.jobTitle}
                  location={formData.location}
                  companyNameOverride={formData.companyNameOverride}
                  emailOverride={formData.emailOverride}
                  phoneOverride={formData.phoneOverride}
                  profileLanguage={formData.profileLanguage}
                  errors={errors}
                  onInputChange={handleInputChange}
                  t={t}
                />

                <AchievementsSection
                  achievements={formData.achievements}
                  newAchievement={newAchievement}
                  editingIndex={editingAchievementIndex}
                  onNewAchievementChange={setNewAchievement}
                  onAddAchievement={handleAddAchievement}
                  onEditAchievement={handleEditAchievement}
                  onUpdateAchievement={handleUpdateAchievement}
                  onSaveAchievementEdit={handleSaveAchievementEdit}
                  onDeleteAchievement={handleDeleteAchievement}
                  onMoveAchievement={handleMoveAchievement}
                  t={t}
                />

                <SocialLinksSection
                  socialLinks={formData.socialLinks}
                  onAddLink={handleAddSocialLink}
                  onUpdateLink={handleUpdateSocialLink}
                  onDeleteLink={handleDeleteSocialLink}
                  onMoveLink={handleMoveSocialLink}
                  isFieldAllowed={isFieldAllowed}
                  disabled={isSaving}
                  t={t}
                />

                <PrivacySettings
                  showEmail={formData.showEmail}
                  showPhone={formData.showPhone}
                  showSkills={formData.showSkills}
                  showAchievements={formData.showAchievements}
                  onInputChange={handleInputChange}
                  t={t}
                />
              </div>

              {/* Right Panel - Live Preview */}
              <LivePreview
                formData={formData}
                currentUser={currentUser}
                profileUrl={profileUrl}
                companyBranding={companyBranding}
                selectedPersonaData={selectedPersonaData}
                publicProfileDetail={publicProfileDetail}
                t={t}
              />
            </div>
          )}

          {activeTab === 'interactions' && (
            <InteractionsTab
              selectedPersona={selectedPersona?.toString() || ''}
              contacts={contacts}
              profileId={profile?.id}
            />
          )}

          {/* Sticky Action Bar - only on configuration tab */}
          {activeTab === 'configuration' && (
            <StickyActionBar
              hasUnsavedChanges={hasUnsavedChanges}
              isSaving={isSaving}
              onReset={handleReset}
              onSave={handleSave}
              t={t}
            />
          )}
      </PageLayout>

      {/* Snackbar */}
      <Snackbar
        snackbar={snackbar}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />

      {/* Visibility Acknowledgment Modal */}
      <VisibilityAcknowledgmentModal
        isOpen={showVisibilityAcknowledgment}
        onClose={() => setShowVisibilityAcknowledgment(false)}
        onAcknowledge={handleAcknowledgePublic}
        t={t}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareMenu}
        profileUrl={profileUrl}
        onClose={() => setShowShareMenu(false)}
        onCopyLink={handleCopyLink}
        t={t}
      />

      {/* Copied Toast */}
      <CopiedToast show={copiedToast} t={t} />
    </div>
  );
};

export default ProfileConfigurationCenter;
